import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
// libraries
import { Box, Typography, Table, TableBody, TableContainer, TableCell, TableRow, MenuItem, Button, Zoom, Tooltip, Snackbar, Alert, Rating } from '@mui/material';
import moment from 'moment';
// redux
import { updateError } from 'src/redux/slices/user';
import DemoBlur, { piiTitle } from 'src/components/DemoBlur';
import CalcomAppointmentBadge from '../calcom/CalcomAppointmentBadge';
import { useSelector, dispatch } from '../../redux/store';
import useAppointmentCancellation from '../../hooks/useAppointmentCancellation';
import { generatingFeedbackUrl, updateGeneratedUrl } from '../../redux/slices/feedbackReview';
// components
import { TableHeadCustom, TableMoreMenu } from '../table';
import Iconify from '../Iconify';
import { OpenReSchedulePopup, OpenAttachmentsPopup } from '../fullScreenPopUps';
import NoAppointmentComponent from '../NoAppointmentComponent';
import CustomTableCell from './CustomTableCell';
import AppointmentDepositBadge from '../appointments/AppointmentDepositBadge';
// hooks/constants/utils
import { useCopyToClipboard, useTable, useToggle } from '../../hooks';
import { pastApptTableLabels } from '../../constants/LeadDetailConst';
import { getAssignees } from '../../utils/multiAsignees';
import { capitalizeFirstWord } from '../../utils/capitalizedFunc';
import { getIcon, StatusValue } from '../../utils/appointmentUtils';
import { AppointmentCancellationDialog, AppointmentCancellationMenuItem } from '../AppointmentCancellationControls';
import DentallyAppointmentBadge from '../dentally/DentallyAppointmentBadge';
// styles
import { dateTypoTextStyle, timeTypoTextStyle, methodTypoTextStyle, treatmentTypoStyle, statusTypoTextStyle, commentTextStyle, normalWhiteSpace } from '../../styles/UpcommingAppointments';
import { toAssigneeIds } from '../../utils/toAssigneeIds';

const PastAppointments = ({ pastAppointment, treatmentData, leadDetail }) => {
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const [tableData, setTableData] = useState(pastAppointment);
    const { practiceUser } = useSelector((state) => state.practiceUser);
    const { generatedUrl } = useSelector((state) => state.feedbackReview);

    const [openMenu, setOpenMenuActions] = useState(null);
    const rowData = [];
    const pastRef = useRef();
    const [appoinmentID, setAppoinmentID] = useState(null);
    const [appoinmentSelectData, setAppoinmentSelectData] = useState(null);
    const [open, setOpen] = useState(false);

    const { copy } = useCopyToClipboard();
    const now = moment().format();
    const inputRef = useRef();
    const cancellation = useAppointmentCancellation({ practiceId, leadId: leadDetail?.id, appointments: pastAppointment });

    useEffect(() => {
        if (pastAppointment) {
            pastAppointment?.map((item) => {
                const latestData = item;
                rowData.push({
                    assignees: toAssigneeIds(item?.assignees),
                    Date: moment(latestData?.datetime, 'DD-MM-YYYY').format('ddd, D MMM YYYY'),
                    Time: moment(latestData?.datetime, 'DD-MM-YYYY HH:mm:ss').format('hh:mm A'),
                    Treatment: treatmentData.treatment,
                    method: latestData?.method,
                    Practioner: latestData?.assignees?.length > 0 ? practiceUser?.results?.filter(el => latestData?.assignees?.some((ele) => ele === el.id) || latestData?.assignees?.some((ele) => ele?.id === el.id)) : [],
                    Status: latestData?.status,
                    appointment_id: latestData?.id,
                    dentally: latestData?.dentally || null,
                    calcom: latestData?.calcom || null,
                    datetime: latestData?.datetime,
                    contacted_via: latestData?.contacted_via,
                    type: latestData?.type || '',
                    note: latestData?.note || '',
                    appointmentFeedback: latestData?.appointment_feedback,
                    depositSummary: latestData?.deposit_summary || null,
                });
                return item;
            });
            setTableData(rowData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pastAppointment]);

    const { onSelectAllRows } = useTable();

    const handleOpenMenu = (event, row) => {
        setOpenMenuActions(event.currentTarget);
        pastRef.current = row?.appointment_id;
    };

    const handleCloseMenu = () => {
        setOpenMenuActions(null);
    };

    const handleReScheduleClose = () => {
        onCloseReSchedule();
    };

    const handleAttachmentClose = () => {
        onCloseAttachment();
    };

    const {
        toggle: OpenReSchedule,
        onOpen: onOpenReSchedule,
        onClose: onCloseReSchedule
    } = useToggle();

    const {
        toggle: OpenAttachments,
        onOpen: onOpenAttachment,
        onClose: onCloseAttachment
    } = useToggle();

    useEffect(() => {
        const htmlElement = document.querySelector('body');
        if (OpenAttachments) {
            htmlElement.classList.add('overflow-hidden');
        } else {
            htmlElement.classList.remove('overflow-hidden');
        }
    }, [OpenAttachments]);

    const handlePopup = (appointmentId, appointmentData) => {
        setAppoinmentID(appointmentId);
        setAppoinmentSelectData(appointmentData);
        setTimeout(() => {
            onOpenReSchedule();
            setOpenMenuActions(null);
        }, 800);
    };

    const attachmentPopup = (appointmentId, appointmentData) => {
        setAppoinmentID(appointmentId);
        setAppoinmentSelectData(appointmentData);
        setTimeout(() => {
            onOpenAttachment();
            setOpenMenuActions(null);
        }, 800);
    };

    const handleGenerateUrl = (appointmentId) => {
        const params = {
            practiceId,
            leadId: leadDetail?.id,
            id: appointmentId,
        };
        dispatch(generatingFeedbackUrl(params));
    };

    const handleCopy = (e) => {
        e.stopPropagation();
        dispatch(updateError({ success: true, message: 'Feedback link copied successfully' }));
        copy(generatedUrl);
    };

    const handleClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setOpen(false);
    };

    const openMoreMenu = (e, row) => {
        dispatch(updateGeneratedUrl(''));
        handleOpenMenu(e, row);
    };

    const handleSelectAllRows = (checked) => onSelectAllRows(checked);

    const customIcon = ({ title, icon }) => <Tooltip title={title} placement='bottom' TransitionComponent={Zoom} TransitionProps={{ timeout: 300 }}>
        <Iconify icon={icon} width={18} height={18} />
    </Tooltip>;

    return (
        <Box>
            {pastAppointment && pastAppointment?.length > 0 ?
                <TableContainer sx={{ width: '100%', position: 'relative' }}>
                    <Table>
                        <TableHeadCustom
                            headLabel={pastApptTableLabels}
                            rowCount={tableData?.length}
                            sx={{ ' .MuiTableCell-head': { boxShadow: 'none !important', minWidth: 'auto' } }}
                            onSelectAllRows={handleSelectAllRows}
                        />
                        <TableBody >
                            {tableData !== undefined && tableData?.map((row, index) => (
                                <TableRow key={index} selected={false} sx={{ borderBottom: '1px solid rgba(145, 158, 171, 0.12)' }}>
                                    <CustomTableCell label={row?.Date} style={dateTypoTextStyle} />
                                    <CustomTableCell label={row?.Time} style={timeTypoTextStyle} />
                                    <TableCell>
                                        <Box sx={{ ml: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                                            <Typography variant='span' noWrap sx={treatmentTypoStyle}>{row?.Treatment}</Typography>
                                            <AppointmentDepositBadge depositSummary={row?.depositSummary} />
                                        </Box>
                                    </TableCell>
                                    <CustomTableCell label={getIcon(row, customIcon)} style={methodTypoTextStyle} />
                                    <CustomTableCell label={capitalizeFirstWord(row?.type)} style={methodTypoTextStyle} />
                                    <TableCell>
                                        <Box sx={{ ml: 0 }}>
                                            <Typography variant='span' noWrap sx={statusTypoTextStyle}>
                                                <DemoBlur>
<Tooltip title={piiTitle(getAssignees({ assignees: row?.Practioner }))} placement='bottom' TransitionComponent={Zoom} TransitionProps={{ timeout: 300 }}>
                                                    <span style={{ textTransform: 'capitalize', marginRight: row?.Practioner?.[0]?.user?.salutation ? '4px' : '0' }}>{`${row?.Practioner?.[0]?.user?.salutation || ''}`.toLowerCase()}</span>{row.Practioner?.[0]?.user?.first_name} {row.Practioner?.[0]?.user?.last_name} {row?.Practioner?.length > 1 ? `+ ${row?.Practioner?.length - 1}` : ''}
                                                </Tooltip>
</DemoBlur>
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        {row?.appointmentFeedback?.rating && <Box sx={{ mb: '4px' }}>
                                            <Rating name='size-small' defaultValue={row?.appointmentFeedback?.rating} size='small' readOnly />
                                        </Box>}
                                        <Typography variant='span' sx={commentTextStyle}>
                                            {row?.appointmentFeedback?.comment || ''}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ ml: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                                            <Typography variant='span' noWrap sx={statusTypoTextStyle}>
                                                {StatusValue(row?.Status)}
                                            </Typography>
                                            {/* Provider identity / creation-cancellation state available today; not a sync verdict. */}
                                            {!row.calcom && <DentallyAppointmentBadge dentally={row?.dentally} />}
                                            <CalcomAppointmentBadge calcom={row.calcom} practiceId={practiceId} leadId={leadDetail?.id} appointmentId={row.appointment_id} />
                                        </Box>
                                    </TableCell>
                                    <TableCell align='right' >
                                        {row?.Status !== 'CANCELLED' && <TableMoreMenu
                                            open={pastRef.current === row.appointment_id && openMenu}
                                            type={'AppointmentRow'}
                                            onOpen={(e) => openMoreMenu(e, row)}
                                            onClose={handleCloseMenu}
                                            actions={
                                                <Box>
                                                    {row.Status !== 'CANCELLED' &&
                                                        <MenuItem disabled={Boolean(row.calcom && !row.calcom.can_reschedule)} onClick={() => handlePopup(row?.appointment_id, row)} sx={normalWhiteSpace}>
                                                            Edit
                                                        </MenuItem>
                                                    }
                                                    <AppointmentCancellationMenuItem controller={cancellation} row={row} />
                                                    <MenuItem onClick={() => attachmentPopup(row?.appointment_id, row)} sx={normalWhiteSpace}>
                                                        View detail
                                                    </MenuItem>
                                                    {moment(row?.datetime, 'DD-MM-YYYY HH:mm:ss').format() < now && <MenuItem onClick={() => handleGenerateUrl(row.appointment_id)} sx={normalWhiteSpace}>
                                                        Generate feedback url {generatedUrl !== '' && <Button variant='contained' onClick={(e) => handleCopy(e)} sx={{ ml: 1, fontSize: '13px', minWidth: '48px' }}>Copy</Button>}
                                                    </MenuItem>}
                                                </Box>
                                            }
                                        />}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                : <NoAppointmentComponent />
            }
            <Snackbar open={open} autoHideDuration={3000} onClose={handleClose}>
                <Alert onClose={handleClose} severity='success'>
                    Text copied to clipboard!
                </Alert>
            </Snackbar>
            <AppointmentCancellationDialog controller={cancellation} />
            {OpenReSchedule && <OpenReSchedulePopup
                open={OpenReSchedule}
                headerButtons
                onClose={handleReScheduleClose}
                treatmentData={treatmentData}
                inputRef={inputRef}
                appoinmentID={appoinmentID}
                appoinmentSelectData={appoinmentSelectData}
                pastAppointment={'pastAppointment'}
                leadDetail={leadDetail}
            />}
            {OpenAttachments && <OpenAttachmentsPopup
                open={OpenAttachments}
                headerButtons
                onClose={handleAttachmentClose}
                inputRef={inputRef}
                appoinmentID={appoinmentID}
            />}
        </Box>
    );
};

export default PastAppointments;

PastAppointments.propTypes = {
    pastAppointment: PropTypes.array,
    treatmentData: PropTypes.object,
    leadDetail: PropTypes.object,
};
